import {
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import React from "react";
import { Controller } from "react-hook-form";

import { Button } from "../../../components";
import { encodeBase64String } from "../../../utils/base64";
import {
  AiInterviewEmailType,
  AiInterviewerFragment,
  useSendTestAiInterviewEmailMutation,
} from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import AiInterviewerSectionTitle from "./AiInterviewerSectionTitle";
import EmailBodyEditor from "./EmailBodyEditor/EmailBodyEditor";
import NotificationDisabledAlert from "./NotificationDisabledAlert";
import { useAiInterviewerFormContext } from "./useAiInterviewerFormContext";

const AiInterviewerNotificationsCompletionSettings: React.FC<{
  aiInterviewer: AiInterviewerFragment;
  readOnly: boolean;
}> = ({ aiInterviewer, readOnly }) => {
  const { register, watch, control } = useAiInterviewerFormContext();
  const completionEmailEnabled = watch("completionEmailEnabled");
  const currentUser = useCurrentUser();
  const toast = useToast();

  // The form is disabled if the notification is turned off OR we're read-only.
  // The two states are visually identical except the disabled state shows an
  // info alert at the top of the form.
  const isDisabled = readOnly || !completionEmailEnabled;

  // Get the completion email config (always present with defaults if not customized)
  const config = aiInterviewer.currentVersion.completionEmailConfig;

  // Available wildcards for completion emails
  const availableWildcards = [
    { name: "candidate_name", label: "Candidate's name" },
    { name: "position_name", label: "Position name" },
  ];

  // Mutation for sending test email
  const [sendTestEmail, { loading: sendingTestEmail }] =
    useSendTestAiInterviewEmailMutation({
      onCompleted: (data) => {
        toast({
          title: "Test email sent",
          description: `Test completion email sent to ${
            data.sendTestAiInterviewEmail?.recipientEmail ?? currentUser.email
          }`,
          status: "success",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      },
      onError: (error) => {
        toast({
          title: "Failed to send test email",
          description: error.message,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      },
    });

  const handleSendTestEmail = () => {
    // Get current form values for the completion email config
    const completionData = watch("emailConfig.completion");

    // Encode the body HTML to base64 for transmission
    const completionWithEncodedHtml = {
      ...completionData,
      bodyHtml: encodeBase64String(completionData.bodyHtml) ?? "",
    };

    sendTestEmail({
      variables: {
        aiInterviewerId: aiInterviewer.id,
        emailType: AiInterviewEmailType.Completion,
        emailConfig: JSON.stringify(completionWithEncodedHtml),
      },
    });
  };

  return (
    <VStack spacing={4} align="stretch" w="100%">
      <HStack justifyContent="space-between" alignItems="center">
        <VStack spacing={1}>
          <AiInterviewerSectionTitle mb={0} title="Completion email" />
          <Flex flexDirection="column">
            <Text color="gray.600" fontSize="sm">
              Customize the confirmation email sent to candidates after they
              complete their AI-moderated interview.
            </Text>
          </Flex>
        </VStack>
        <Button
          onClick={handleSendTestEmail}
          isLoading={sendingTestEmail}
          isDisabled={!completionEmailEnabled}
          colorScheme="blue"
          variant="outline"
          size="sm"
        >
          Send Test Email
        </Button>
      </HStack>
      {!completionEmailEnabled && (
        <NotificationDisabledAlert notificationName="Completion email" />
      )}
      <FormControl id="fromEmailName">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          From email name
        </FormLabel>
        <Input
          {...register("emailConfig.completion.fromName")}
          defaultValue={config.fromName}
          fontSize="sm"
          borderRadius="md"
          isDisabled={isDisabled}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="fromEmailAddress">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          From email address
        </FormLabel>
        <Input
          {...register("emailConfig.completion.fromEmail")}
          defaultValue={config.fromEmail}
          fontSize="sm"
          borderRadius="md"
          isDisabled
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="replyToEmailAddress">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Reply-to email address
        </FormLabel>
        <Input
          {...register("emailConfig.completion.replyTo")}
          defaultValue={config.replyTo}
          fontSize="sm"
          borderRadius="md"
          isDisabled={isDisabled}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="emailSubject">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Email subject
        </FormLabel>
        <Input
          {...register("emailConfig.completion.subject")}
          fontSize="sm"
          borderRadius="md"
          isDisabled={isDisabled}
          _disabled={{
            bg: "gray.50",
          }}
        />
      </FormControl>
      <FormControl id="emailBody">
        <FormLabel fontSize="sm" mb={1} color="gray.600">
          Email body
        </FormLabel>
        <Controller
          name="emailConfig.completion.bodyHtml"
          control={control}
          defaultValue={config.bodyHtml}
          render={({ field }) => (
            <EmailBodyEditor
              value={field.value}
              fieldName={field.name}
              onChange={field.onChange}
              readOnly={isDisabled}
              placeholder="Enter email body..."
              availableWildcards={availableWildcards}
            />
          )}
        />
      </FormControl>
    </VStack>
  );
};

export default AiInterviewerNotificationsCompletionSettings;
